import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { useAlert } from '../components/AlertProvider';

interface Comment {
    id: number;
    content: string;
    original_locale: string;
    like_count: number;
    created_at: string;
    author: {
        anonymousId: string;
        country: string;
        countryFlag: string;
    };
    isLikedByMe: boolean;
    translationIndicator: string | null;
    parent_comment_id: number | null;
    replies?: Comment[];
}

interface PostDetail {
    id: number;
    content: string;
    original_locale: string;
    hashtagsArray: string[];
    like_count: number;
    comment_count: number;
    view_count: number;
    created_at: string;
    author: {
        anonymousId: string;
        country: string;
        countryFlag: string;
        interests: string[];
        mbti: string | null;
        ageRange: string;
        genderHint: string | null;
    };
    isLikedByMe: boolean;
    translationIndicator: string | null;
    comments: Comment[];
}

export default function PostDetailPage() {
    const { postId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert } = useAlert();

    const [post, setPost] = useState<PostDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [commentContent, setCommentContent] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [replyTo, setReplyTo] = useState<{ id: number; authorId: string } | null>(null);

    useEffect(() => {
        if (postId) {
            fetchPostDetail(parseInt(postId));
        }
    }, [postId]);

    const fetchPostDetail = async (id: number) => {
        try {
            setLoading(true);
            const data = await api.community.getPost(id);
            if (data.success) {
                // Merge post with comments since API returns them separately
                setPost({ ...data.post, comments: data.comments || [] });
            } else {
                showAlert(data.error || t('community.error_fetch_post', 'Failed to load post'));
                navigate('/community');
            }
        } catch (error: any) {
            console.error('Failed to fetch post:', error);
            showAlert(error.message || t('community.error_fetch_post', 'Failed to load post'));
            navigate('/community');
        } finally {
            setLoading(false);
        }
    };

    const handleLikePost = async () => {
        if (!post) return;
        try {
            const data = post.isLikedByMe
                ? await api.community.unlikePost(post.id)
                : await api.community.likePost(post.id);

            if (data.success) {
                setPost({ ...post, like_count: data.likeCount, isLikedByMe: data.isLiked });
            }
        } catch (error: any) {
            console.error('Failed to like post:', error);
            showAlert(error.message || t('community.error_like', 'Failed to like post'));
        }
    };

    const handleSubmitComment = async () => {
        if (!post || commentContent.trim().length === 0) return;

        try {
            setSubmittingComment(true);
            const data = await api.community.createComment(
                post.id,
                commentContent,
                replyTo?.id
            );

            if (data.success) {
                setCommentContent('');
                setReplyTo(null);
                fetchPostDetail(post.id); // Refresh to show new comment
                showAlert(t('community.comment_created', 'Comment posted!'));
            } else {
                showAlert(data.error || t('community.error_comment', 'Failed to post comment'));
            }
        } catch (error: any) {
            console.error('Failed to create comment:', error);
            showAlert(error.message || t('community.error_comment', 'Failed to post comment'));
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleLikeComment = async (commentId: number, isLiked: boolean) => {
        if (!post) return;
        try {
            const data = isLiked
                ? await api.community.unlikeComment(commentId)
                : await api.community.likeComment(commentId);

            if (data.success) {
                // Deep update comments tree
                const updateComments = (comments: Comment[]): Comment[] => {
                    return comments.map(c => {
                        if (c.id === commentId) {
                            return { ...c, like_count: data.likeCount, isLikedByMe: data.isLiked };
                        }
                        if (c.replies) {
                            return { ...c, replies: updateComments(c.replies) };
                        }
                        return c;
                    });
                };

                setPost({ ...post, comments: updateComments(post.comments) });
            }
        } catch (error: any) {
            console.error('Failed to like comment:', error);
            showAlert(error.message || t('community.error_like', 'Failed to like comment'));
        }
    };

    if (loading) return <LoadingState />;
    if (!post) return null;

    return (
        <Layout>
            <div className="min-h-screen bg-gray-50 pb-40 md:pb-20">
                <div className="max-w-2xl mx-auto">
                    {/* Header */}
                    <div className="bg-white sticky top-0 z-10 px-4 py-3 shadow-sm flex items-center gap-4">
                        <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
                            ← {t('common.back', 'Back')}
                        </button>
                        <h1 className="font-bold text-lg">{t('community.post_detail', 'Post')}</h1>
                    </div>

                    {/* Post Content */}
                    <div className="bg-white p-6 mb-4 shadow-sm">
                        <PostHeader author={post.author} createdAt={post.created_at} />
                        <div className="mt-4 mb-6">
                            <p className="text-gray-800 whitespace-pre-wrap text-lg leading-relaxed">{post.content}</p>
                            {post.translationIndicator && (
                                <p className="mt-2 text-sm text-gray-500">{post.translationIndicator}</p>
                            )}
                            {post.hashtagsArray.length > 0 && (
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {post.hashtagsArray.map((tag, i) => (
                                        <span key={i} className="text-purple-600">#{tag}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-6 pt-4 border-t border-gray-100">
                            <button
                                onClick={handleLikePost}
                                className={`flex items-center gap-2 ${post.isLikedByMe ? 'text-pink-500' : 'text-gray-500'}`}
                            >
                                <span className="text-xl">{post.isLikedByMe ? '❤️' : '🤍'}</span>
                                <span className="font-semibold">{post.like_count}</span>
                            </button>
                            <div className="flex items-center gap-2 text-gray-500">
                                <span className="text-xl">💬</span>
                                <span className="font-semibold">{post.comment_count}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-500">
                                <span className="text-xl">👁️</span>
                                <span className="font-semibold">{post.view_count}</span>
                            </div>
                        </div>
                    </div>

                    {/* Comments Section */}
                    <div className="px-4">
                        <h3 className="font-bold text-gray-700 mb-4">
                            {t('community.comments', 'Comments')} ({post.comments?.length || 0})
                        </h3>

                        <div className="space-y-4 mb-20">
                            {(post.comments || []).map(comment => (
                                <CommentItem
                                    key={comment.id}
                                    comment={comment}
                                    onLike={handleLikeComment}
                                    onReply={(id, authorId) => setReplyTo({ id, authorId })}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Comment Input */}
                    <div className="fixed bottom-[70px] md:bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40">
                        <div className="max-w-2xl mx-auto">
                            {replyTo && (
                                <div className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded-lg mb-2 text-sm">
                                    <span className="text-gray-600">
                                        Replying to <b>{replyTo.authorId}</b>
                                    </span>
                                    <button onClick={() => setReplyTo(null)} className="text-gray-500 hover:text-gray-700">✕</button>
                                </div>
                            )}
                            <div className="flex gap-2">
                                <textarea
                                    value={commentContent}
                                    onChange={(e) => setCommentContent(e.target.value)}
                                    placeholder={t('community.write_comment', 'Write a comment...')}
                                    className="flex-1 bg-gray-100 rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500 outline-none resize-none h-[50px]"
                                    disabled={submittingComment}
                                />
                                <button
                                    onClick={handleSubmitComment}
                                    disabled={commentContent.trim().length === 0 || submittingComment}
                                    className="px-4 bg-purple-600 text-white rounded-xl font-semibold disabled:opacity-50"
                                >
                                    {submittingComment ? '...' : '➤'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

// Sub-components
function PostHeader({ author, createdAt }: { author: any, createdAt: string }) {
    const { t } = useTranslation();
    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
        if (seconds < 60) return t('time.just_now', 'Just now');
        if (seconds < 3600) return t('time.minutes_ago', '{{count}}m', { count: Math.floor(seconds / 60) });
        if (seconds < 86400) return t('time.hours_ago', '{{count}}h', { count: Math.floor(seconds / 3600) });
        return t('time.days_ago', '{{count}}d', { count: Math.floor(seconds / 86400) });
    };

    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white text-lg font-bold">
                {author.countryFlag}
            </div>
            <div>
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{author.anonymousId}</span>
                    <span className="text-gray-400 text-sm">• {formatTimeAgo(createdAt)}</span>
                </div>
                <div className="text-xs text-gray-500">
                    {author.mbti} • {author.ageRange}
                </div>
            </div>
        </div>
    );
}

function CommentItem({ comment, onLike, onReply }: {
    comment: Comment,
    onLike: (id: number, isLiked: boolean) => void,
    onReply: (id: number, authorId: string) => void
}) {
    const { t } = useTranslation();
    const [showOriginal, setShowOriginal] = useState(false);

    return (
        <div className="flex gap-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm">
                {comment.author.countryFlag}
            </div>
            <div className="flex-1">
                <div className="bg-white p-3 rounded-2xl rounded-tl-none shadow-sm border border-gray-100">
                    <div className="flex justify-between items-start mb-1">
                        <span className="font-semibold text-sm text-gray-800">{comment.author.anonymousId}</span>
                        <span className="text-xs text-gray-400">{new Date(comment.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{comment.content}</p>

                    {comment.translationIndicator && (
                        <div className="mt-1">
                            <button
                                onClick={() => setShowOriginal(!showOriginal)}
                                className="text-xs text-purple-500 hover:underline"
                            >
                                {showOriginal ? t('community.show_translation', 'Show Translation') : comment.translationIndicator}
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-4 mt-1 ml-2">
                    <button
                        onClick={() => onLike(comment.id, comment.isLikedByMe)}
                        className={`text-xs font-medium flex items-center gap-1 ${comment.isLikedByMe ? 'text-pink-500' : 'text-gray-500'}`}
                    >
                        {comment.isLikedByMe ? '❤️' : '🤍'} {comment.like_count > 0 && comment.like_count}
                    </button>
                    <button
                        onClick={() => onReply(comment.id, comment.author.anonymousId)}
                        className="text-xs text-gray-500 font-medium hover:text-gray-800"
                    >
                        {t('community.reply', 'Reply')}
                    </button>
                </div>

                {/* Nested Replies */}
                {comment.replies && comment.replies.length > 0 && (
                    <div className="mt-3 space-y-3 pl-3 border-l-2 border-gray-100">
                        {comment.replies.map(reply => (
                            <CommentItem
                                key={reply.id}
                                comment={reply}
                                onLike={onLike}
                                onReply={onReply}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function LoadingState() {
    return (
        <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
        </div>
    );
}
